import { logUserAction } from "../utils/telemetry";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import "../firebase/client";

const AI_FUNCTIONS_REGION = "europe-west1";

function aiFunctions() {
  return getFunctions(getApp(), AI_FUNCTIONS_REGION);
}

function callableErrorSignal(error: unknown): string {
  if (!error || typeof error !== "object") return String(error || "").toLowerCase();
  const code = String(Reflect.get(error, "code") || "");
  const message = String(Reflect.get(error, "message") || "");
  return `${code} ${message}`.toLowerCase();
}

function isRetryableCallableError(error: unknown): boolean {
  const signal = callableErrorSignal(error);
  return (
    signal.includes("functions/unavailable") ||
    signal.includes("functions/internal") ||
    signal.includes("functions/deadline-exceeded") ||
    signal.includes("network") ||
    signal.includes("failed to fetch")
  );
}

async function callAiFunction<TRequest, TResponse>(name: string, request: TRequest): Promise<TResponse> {
  const payload = { ...request, requestId: crypto.randomUUID() };
  let lastError: unknown = null;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const callable = httpsCallable<TRequest, TResponse>(aiFunctions(), name, { timeout: 180000 });
      const response = await callable(payload);
      return response.data;
    } catch (error) {
      lastError = error;
      if (callableErrorSignal(error).includes("mundely_ai_limit")) void logUserAction("plan_ai_limit_reached", "billing");
      if (!isRetryableCallableError(error) || attempt === 2) throw error;
      await new Promise<void>((resolve) => window.setTimeout(resolve, attempt === 0 ? 450 : 1100));
    }
  }
  throw lastError;
}

export type AiCapability = "assistant" | "itinerary" | "document" | "summary" | "translation" | "suggestions" | "photoSelection";
export type AiActionMode = "none" | "execute" | "confirm";
export type AiActionOperation = "none" | "create" | "update" | "delete";
export type AiActionSection =
  | "none"
  | "trip"
  | "documents"
  | "workspace"
  | "hotels"
  | "restaurants"
  | "places"
  | "pointsOfInterest"
  | "notes"
  | "itinerary"
  | "experiences"
  | "events"
  | "climate"
  | "settings";

export type AiWritableActionSection = Exclude<AiActionSection, "none" | "trip" | "documents" | "workspace" | "settings">;

export interface AiAttachmentPayload {
  name: string;
  mimeType: string;
  dataUrl: string;
  size?: number;
}

export interface AiAgentAction {
  mode: AiActionMode;
  operation: AiActionOperation;
  section: AiActionSection;
  tripName: string;
  targetId: string;
  payloadJson: string;
  attachmentName: string;
  description: string;
}

export interface AiGatewayRequest {
  capability: AiCapability;
  input: string;
  language?: "it" | "en" | "es";
  tripId?: string;
  contextRefs?: string[];
  attachments?: AiAttachmentPayload[];
}

export interface AiGatewayResponse {
  requestId: string;
  status: "ok" | "needs_context" | "rejected" | "disabled";
  output: string;
  inferredTripName?: string;
  action?: AiAgentAction;
}

export async function askTripAI(request: AiGatewayRequest): Promise<AiGatewayResponse> {
  return callAiFunction<AiGatewayRequest, AiGatewayResponse>("tripAiAgentGateway", request);
}

export interface AiRealtimeHistoryItem {
  role: "user" | "assistant";
  text: string;
}

export interface AiRealtimeSessionRequest {
  language: "it" | "en" | "es";
  travelContext: string;
  activeTripName?: string;
  chatHistory?: AiRealtimeHistoryItem[];
}

export interface AiRealtimeSessionResponse {
  value: string;
  expiresAt?: number;
  model: string;
}

export async function createTripAiRealtimeSession(request: AiRealtimeSessionRequest): Promise<AiRealtimeSessionResponse> {
  return callAiFunction<AiRealtimeSessionRequest, AiRealtimeSessionResponse>("createTripAiRealtimeSession", request);
}
